import {
  Alignment,
  AutoImage,
  Base64UploadAdapter,
  BlockQuote,
  Bold,
  Bookmark,
  ClassicEditor,
  Code,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  Image,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

import { CKEditor } from '@ckeditor/ckeditor5-react';

interface CKEditorWrapperProps {
  content: string;
  onChange: (data: string) => void;
  editable?: boolean;
}

const CKEditorWrapper = ({ content, onChange, editable = true }: CKEditorWrapperProps) => {
  return (
    <div className={editable ? 'border rounded-md' : ''}>
      <CKEditor
        editor={ClassicEditor}
        config={{
          licenseKey: 'GPL',
          plugins: [
            Essentials,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Subscript,
            Superscript,
            Code,
            Paragraph,
            Heading,
            Link,
            LinkImage,
            Bookmark,
            List,
            BlockQuote,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
            HorizontalLine,
            PageBreak,
            Indent,
            IndentBlock,
            Alignment,
            FontFamily,
            FontSize,
            FontColor,
            FontBackgroundColor,
            RemoveFormat,
            Image,
            ImageToolbar,
            ImageStyle,
            ImageResize,
            ImageUpload,
            AutoImage,
            Base64UploadAdapter,
            PasteFromOffice,
            GeneralHtmlSupport,
            Undo,
            Mention,
          ],
          toolbar: editable
            ? [
                'undo',
                'redo',
                '|',
                'heading',
                '|',
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'code',
                'removeFormat',
                '|',
                'fontFamily',
                'fontSize',
                'fontColor',
                'fontBackgroundColor',
                '|',
                'alignment',
                '|',
                'link',
                'bookmark',
                'bulletedList',
                'numberedList',
                '|',
                'outdent',
                'indent',
                '|',
                'blockQuote',
                'insertTable',
                'horizontalLine',
                'pageBreak',
              ]
            : [],
          heading: {
            options: [
              { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
              { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
              { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
              { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
              { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
              { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
              { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
            ],
          },
          fontFamily: {
            supportAllValues: true,
          },
          fontSize: {
            options: [9, 10, 11, 12, 'default', 14, 16, 18, 20, 24],
            supportAllValues: true,
          },
          table: {
            contentToolbar: [
              'tableColumn',
              'tableRow',
              'mergeTableCells',
              'tableProperties',
              'tableCellProperties',
            ],
          },
          image: {
            toolbar: [
              'imageStyle:inline',
              'imageStyle:block',
              'imageStyle:side',
              '|',
              'toggleImageCaption',
              'imageTextAlternative',
            ],
          },
          // Preserve markup produced by Word/paste that has no dedicated plugin
          // (styles, classes, spans, anchors, etc.) instead of stripping it.
          htmlSupport: {
            allow: [
              {
                name: /^(?!script$|style$|iframe$|object$|embed$|form$|input$|textarea$|select$|button$|link$|meta$|base$).*$/,
                attributes: true,
                classes: true,
                styles: true,
              },
            ],
            disallow: [
              { attributes: [{ key: /^on/i, value: true }] },
              { name: 'a', attributes: [{ key: 'href', value: /^\s*javascript:/i }] },
              { name: 'img', attributes: [{ key: 'src', value: /^\s*javascript:/i }] },
            ],
          },
        }}
        data={content}
        onChange={(_event, editor) => {
          if (editable) {
            const data = editor.getData();
            onChange(data);
          }
        }}
        disabled={!editable}
      />
    </div>
  );
};

export default CKEditorWrapper;
